import {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Eyebrow from '@/components/Common/Eyebrow';
import StatCallout from '@/components/Common/StatCallout';
import type {StatCalloutProps} from '@/components/Common/StatCallout';
import FeatureIconCard from '@/components/shared/cards/FeatureIconCard';
import type {FeatureIconCardProps} from '@/components/shared/cards/FeatureIconCard';
import {colors, fontSize, fontWeight, letterSpacing, lineHeight, spacing} from '@/styles/tokens';

export interface SplitIntroCardsProps {
    /** Optional kicker above the heading. Pages that lead with the heading omit it. */
    eyebrow?: string;
    heading: string;
    /** Body paragraphs, stacked in order. */
    paragraphs: string[];
    /** Optional emphasised figure below the paragraphs. */
    callout?: Omit<StatCalloutProps, 'sx'>;
    /** Cards stacked down the right column. The first may be `highlighted`. */
    cards: FeatureIconCardProps[];
}

const gridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
    gap: {xs: 6, md: 8},
    alignItems: 'start'
} as const;

const leftColSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[24]
} as const;

const headingSx = {
    fontSize: {xs: fontSize[32], md: fontSize[48]},
    fontWeight: fontWeight.extrabold,
    color: colors.text.primary,
    letterSpacing: letterSpacing.tight,
    lineHeight: {xs: lineHeight[40], md: lineHeight[56]}
} as const;

const paragraphsWrapSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[24]
} as const;

const paragraphSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

// White rather than the callout's default tint: this section sits on the blue page
// band, so the tinted box lost its contrast against it.
const calloutSx = {mt: spacing[8], bgcolor: colors.background.default} as const;

const cardsColSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[16]
} as const;

/**
 * SplitIntroCards — an explanatory section: heading, body copy and an optional
 * stat callout on the left, a stack of icon cards on the right.
 *
 * Distinct from `ServiceDefinition` (same left column, but a diagram on the
 * right and a CTA in its callout) and from `WhatSetsBest` (same cards, but in a
 * two-up grid beside a checklist card).
 */
const SplitIntroCards = ({eyebrow, heading, paragraphs, callout, cards}: SplitIntroCardsProps) => (
    <MUIContainer>
        <Box sx={gridSx}>
            {/* ── Left: heading, body copy, stat callout ─────────────────── */}
            <Box sx={leftColSx}>
                {eyebrow && <Eyebrow text={eyebrow} sx={{mb: 0}} />}
                <Typography component="h2" className="font-headers" sx={headingSx}>
                    {heading}
                </Typography>
                <Box sx={paragraphsWrapSx}>
                    {paragraphs.map((text) => (
                        <Typography key={text} component="p" className="font-regular-text" sx={paragraphSx}>
                            {text}
                        </Typography>
                    ))}
                </Box>
                {callout && <StatCallout {...callout} sx={calloutSx} />}
            </Box>

            {/* ── Right: card stack ──────────────────────────────────────── */}
            <Box sx={cardsColSx}>
                {cards.map((card) => (
                    <FeatureIconCard key={card.title} {...card} iconTone="light" hoverFill />
                ))}
            </Box>
        </Box>
    </MUIContainer>
);

export default memo(SplitIntroCards);
